import { useAppSelector } from '@hooks/redux.hooks';
import { useDimensions } from '@hooks/useDimensions';
import { selectBaseLayer } from '@redux/config/config.selectors';
import { selectLanguage } from '@redux/settings/settings.selectors';
import { getSettingsLocale } from '@utils/locale.utils';
import { isBaseStyleRaster } from '@utils/map/map.utils';
import { motion } from 'framer-motion';
import { RequestParameters, ResourceType } from 'mapbox-gl';
import { PropsWithChildren, RefObject, useCallback, useState } from 'react';
import { Layer, Map, MapRef, Source, ViewState } from 'react-map-gl/mapbox';

import { LayerNameEnum, MapIdEnum, SourceNameEnum } from '@/types/map.types';

const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
const BASE_BACKGROUND_MAP_URL = import.meta.env.VITE_BASE_BACKGROUND_MAP_URL;
const DEV = import.meta.env.DEV;

interface BaseMapProps {
  mapId: MapIdEnum;
  viewState: ViewState;
  setViewState: (viewState: ViewState) => void;
  mapRef?: RefObject<MapRef | null>;
  mapConstraintsRef?: RefObject<HTMLDivElement | null>;
  selectableLayers?: LayerNameEnum[];
  onLoad?: () => void;
}

function BaseMap({
  mapId,
  mapRef,
  mapConstraintsRef,
  selectableLayers,
  viewState,
  setViewState,
  onLoad,
  children,
}: PropsWithChildren<BaseMapProps>) {
  const [rect, setNode] = useDimensions();
  const [cursor, setCursor] = useState<string>('auto');
  const mapStyle = useAppSelector(selectBaseLayer);
  const language = getSettingsLocale(useAppSelector(selectLanguage));

  const transformStringMapStyle = (url: string) => {
    if (url.includes('http')) return url;
    return `${DEV ? BASE_BACKGROUND_MAP_URL : globalThis.location.origin}${url}`;
  };

  const handleTransformRequest = (url: string, resourceTypeEnum?: ResourceType) => {
    if (resourceTypeEnum === 'SpriteJSON' || resourceTypeEnum === 'SpriteImage' || resourceTypeEnum === 'Glyphs') {
      return {
        url: url.replace('data:/', ''),
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      } as RequestParameters;
    }
    return { url };
  };

  const handleMove = useCallback(
    (event: { viewState: unknown }) => {
      setViewState(event.viewState as ViewState);
    },
    [setViewState],
  );

  const handleMouseEnter = useCallback(() => setCursor('pointer'), []);
  const handleMouseLeave = useCallback(() => setCursor('auto'), []);

  return (
    <motion.div
      ref={(node) => {
        setNode(node);
        if (mapConstraintsRef) {
          mapConstraintsRef.current = node!;
        }
      }}
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: 'neutral.900',
        position: 'relative',
      }}
    >
      {rect && rect.width > 0 && rect.height > 0 && (
        <Map
          {...viewState}
          ref={mapRef}
          id={mapId}
          style={{ width: '100%', height: '100%' }}
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
          attributionControl={false}
          mapStyle={isBaseStyleRaster(mapStyle) ? mapStyle.baseStyle : mapStyle}
          language={language}
          pitch={0}
          pitchWithRotate={false}
          transformRequest={handleTransformRequest}
          cursor={cursor}
          interactiveLayerIds={selectableLayers}
          onMove={handleMove}
          onLoad={onLoad}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {isBaseStyleRaster(mapStyle) && (
            <Source
              id={SourceNameEnum.BASE_LAYER}
              type="raster"
              tiles={[transformStringMapStyle(mapStyle.url)]}
              tileSize={256}
            >
              <Layer id={LayerNameEnum.BASE_LAYER} type="raster" />
            </Source>
          )}
          {children}
        </Map>
      )}
    </motion.div>
  );
}

export default BaseMap;
